import { Collections, Race, RaceControl, Round, RoundDefault } from "api/model";
import cloneDeep from "lodash/cloneDeep";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { isLoaded, useFirestoreConnect } from "react-redux-firebase";
import { RootState } from "redux/model";

interface UseRoundsOptions {
    championshipId: string;
}

export const useRounds = ({ championshipId }: UseRoundsOptions): Round[] => {
    const storeAs = `${Collections.ROUNDS}${championshipId}`;
    useFirestoreConnect({
        collection: Collections.ROUNDS,
        where: [["championshipId", "==", championshipId || ""]],
        storeAs,
    });
    const rounds: Round[] =
        cloneDeep(
            useSelector((state: RootState) => state.firestore.ordered[storeAs])
        ) || [];
    if (rounds) {
        rounds.sort(
            (r1, r2) =>
                DateTime.fromISO(r1.date).toMillis() -
                DateTime.fromISO(r2.date).toMillis()
        );
        rounds.forEach((round, index) => {
            round.roundNum = index + 1;
        });
    }

    return rounds;
};

interface UseRoundOptions {
    roundId: string;
    withRaces?: boolean;
    withRacesControl?: boolean;
}

interface UseRoundResult {
    round: Round;
    loading: boolean | undefined;
}

export const useRound = ({
    roundId,
    withRaces,
    withRacesControl,
}: UseRoundOptions): UseRoundResult => {
    const queriesConfig: any = [
        {
            collection: Collections.ROUNDS,
            doc: roundId,
        },
    ];

    if (withRaces || withRacesControl) {
        queriesConfig.push({
            collection: Collections.RACES,
            where: [["roundId", "==", roundId]],
        });
    }

    if (withRacesControl) {
        queriesConfig.push({
            collection: Collections.RACES_CONTROL,
            where: [["roundId", "==", roundId]],
        });
    }

    useFirestoreConnect(queriesConfig);

    const result: UseRoundResult = cloneDeep(
        useSelector(({ firestore: { data, ordered } }: RootState) => {
            const rounds = cloneDeep<Round[]>(data[Collections.ROUNDS]);
            let races = cloneDeep<Race[]>(ordered[Collections.RACES]) || [];
            let racesControl =
                cloneDeep<RaceControl[]>(ordered[Collections.RACES_CONTROL]) ||
                [];

            races = races.filter((race) => race.roundId === roundId);
            racesControl = racesControl.filter((rc) => rc.roundId === roundId);

            const loading =
                !isLoaded(rounds) ||
                (withRaces && !isLoaded(races)) ||
                (withRacesControl && !isLoaded(racesControl));

            let result: Round = RoundDefault;

            if (rounds) {
                result = { ...RoundDefault, ...rounds[roundId], id: roundId };
            }

            if (result.id && races && races.length > 0) {
                if (racesControl && racesControl.length > 0) {
                    racesControl.forEach((rc) => {
                        races.forEach((race) => {
                            if (race.id === rc.raceId) {
                                race.raceControl = rc;
                                return;
                            }
                        });
                    });
                }
                result.races = races;
            }

            return { round: result, loading };
        })
    );

    return result;
};
